/**
 * Collapses product-details app blocks that have no visible content so they
 * do not keep a slot in the flex gap. Blocks are shown again when an app renders.
 */

const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT', 'TEMPLATE']);
const MEDIA_SELECTOR = 'img, svg, iframe, canvas, video, picture, input, button, select, textarea';
const MEASURE_DELAYS = [400, 1200, 2500];

const hasText = (root) => {
  for (const node of root.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) return true;
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    if (IGNORED_TAGS.has(node.tagName)) continue;
    if (node.shadowRoot && hasText(node.shadowRoot)) return true;
    if (hasText(node)) return true;
  }

  return false;
};

const hasCustomElement = (block) => {
  for (const node of block.querySelectorAll('*')) {
    if (node.tagName.includes('-')) return true;
  }

  return false;
};

const customElementHasContent = (block) => {
  for (const node of block.querySelectorAll('*')) {
    if (!node.tagName.includes('-')) continue;
    if (node.shadowRoot && (hasText(node.shadowRoot) || node.shadowRoot.querySelector(MEDIA_SELECTOR))) return true;
    if (node.getBoundingClientRect().height > 1) return true;
  }

  return false;
};

const hasVisibleContent = (block, measureCustomElements) => {
  if (hasText(block)) return true;
  if (block.querySelector(MEDIA_SELECTOR)) return true;
  if (!hasCustomElement(block)) return false;
  if (!measureCustomElements) return true;

  return customElementHasContent(block);
};

class ProductDetailsComponent extends HTMLElement {
  #observer;
  #shadowRoots = new WeakSet();
  #timers = [];
  #frame = 0;
  #allowMeasure = false;

  connectedCallback() {
    if (window.Shopify?.designMode) return;

    this.setAttribute('data-collapse-empty-apps', '');
    this.#observer = new MutationObserver(() => this.#scheduleSync());
    this.#sync();
    this.#observe(this);

    for (const delay of MEASURE_DELAYS) {
      this.#timers.push(window.setTimeout(() => {
        this.#allowMeasure = true;
        this.#sync();
      }, delay));
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);

    for (const timer of this.#timers) window.clearTimeout(timer);
    this.#timers = [];
  }

  #scheduleSync() {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#sync();
    });
  }

  #observe(root) {
    this.#observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.#watchShadows(root);
  }

  #watchShadows(root) {
    if (!root.querySelectorAll) return;

    for (const node of root.querySelectorAll('*')) {
      if (!node.shadowRoot || this.#shadowRoots.has(node.shadowRoot)) continue;

      this.#shadowRoots.add(node.shadowRoot);
      this.#observe(node.shadowRoot);
    }
  }

  #sync() {
    for (const block of this.querySelectorAll('.shopify-app-block')) {
      this.#watchShadows(block);

      const shouldMeasure = this.#allowMeasure && hasCustomElement(block);
      if (shouldMeasure && block.hidden) block.hidden = false;

      block.hidden = !hasVisibleContent(block, shouldMeasure);
    }
  }
}

if (!customElements.get('product-details-component')) {
  customElements.define('product-details-component', ProductDetailsComponent);
}
